import { useState, useEffect, useRef } from "react";
import { Sparkles, Check, X, Loader2, ChevronDown, ChevronUp } from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

/**
 * GifProgressIndicator - A floating indicator showing active GIF generation jobs
 * Place this component at the app root level to show progress anywhere
 */
const GifProgressIndicator = ({ onGifReady }) => {
  const [activeJobs, setActiveJobs] = useState([]);
  const [completedGifs, setCompletedGifs] = useState([]);
  const [isExpanded, setIsExpanded] = useState(false);
  const jobsRef = useRef(activeJobs);
  const onReadyRef = useRef(onGifReady);
  onReadyRef.current = onGifReady;

  // Poll for active jobs
  useEffect(() => {
    let stopped = false;
    let running = false;
    const checkActiveJobs = async () => {
      if (running || stopped) return;
      running = true;
      try {
        const token = localStorage.getItem('parseeker_session_token');
        if (!token) return;

        const response = await fetch(`${API}/gif/active-jobs`, {
          headers: { 'X-Session-Token': token }
        });
        
        if (response.ok && !stopped) {
          const data = await response.json();
          const jobs = data.jobs || [];
          
          // Check for newly completed jobs
          for (const prevJob of jobsRef.current) {
            const currentJob = jobs.find(j => j.job_id === prevJob.job_id);
            if (!currentJob && prevJob.status !== 'completed' && prevJob.status !== 'failed') {
              // Job disappeared from active - check its final status
              const statusRes = await fetch(`${API}/gif/status/${prevJob.job_id}`);
              if (statusRes.ok && !stopped) {
                const finalStatus = await statusRes.json();
                if (finalStatus.status === 'completed') {
                  setCompletedGifs(prev => [...prev, finalStatus]);
                  toast.success("Your Golf GIF is ready! 🎉", {
                    action: {
                      label: "View",
                      onClick: () => onReadyRef.current?.(finalStatus)
                    }
                  });
                }
              }
            }
          }
          
          if (stopped) return;
          jobsRef.current = jobs;
          setActiveJobs(jobs);
        }
      } catch (err) {
        console.error("Error checking GIF jobs:", err);
      } finally {
        running = false;
      }
    };

    // Initial check
    checkActiveJobs();

    // Poll every 5 seconds
    const interval = setInterval(checkActiveJobs, 5000);
    return () => { stopped = true; clearInterval(interval); };
  }, []);

  // Hide completed GIFs after viewing
  const dismissCompletedGif = (jobId) => {
    setCompletedGifs(prev => prev.filter(g => g.job_id !== jobId));
  };

  if (activeJobs.length === 0 && completedGifs.length === 0) {
    return null;
  }

  const totalJobs = activeJobs.length + completedGifs.length;

  return (
    <div className="fixed bottom-20 right-4 z-50">
      {/* Collapsed View - Just the badge */}
      {!isExpanded && totalJobs > 0 && (
        <button
          onClick={() => setIsExpanded(true)}
          className="flex items-center gap-2 bg-gradient-to-r from-green-600 to-green-500 text-white px-4 py-2 rounded-full shadow-lg hover:shadow-xl transition-all animate-pulse"
        >
          {activeJobs.length > 0 ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <Sparkles className="w-4 h-4" />
          )}
          <span className="text-sm font-medium">
            {activeJobs.length > 0 
              ? `Creating GIF${activeJobs.length > 1 ? 's' : ''}...` 
              : `${completedGifs.length} GIF${completedGifs.length > 1 ? 's' : ''} ready!`
            }
          </span>
          <ChevronUp className="w-4 h-4" />
        </button>
      )}

      {/* Expanded View - Show details */}
      {isExpanded && (
        <div className="bg-[#1a1a1a] border border-gray-700 rounded-lg shadow-2xl w-80 overflow-hidden">
          {/* Header */}
          <div 
            className="flex items-center justify-between p-3 bg-gradient-to-r from-green-600/20 to-green-500/10 border-b border-gray-700 cursor-pointer"
            onClick={() => setIsExpanded(false)}
          >
            <div className="flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-green-500" />
              <span className="text-white font-medium text-sm">Golf GIF Generator</span>
            </div>
            <ChevronDown className="w-4 h-4 text-gray-400" />
          </div>

          {/* Jobs List */}
          <div className="max-h-64 overflow-y-auto">
            {/* Active Jobs */}
            {activeJobs.map(job => (
              <div key={job.job_id} className="p-3 border-b border-gray-800">
                <div className="flex items-start gap-3">
                  <div className="mt-1">
                    <Loader2 className="w-4 h-4 text-green-500 animate-spin" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-white text-sm truncate">
                      {job.prompt?.slice(0, 40)}...
                    </p>
                    <p className="text-gray-500 text-xs mt-1">
                      {job.progress || job.status}
                    </p>
                  </div>
                </div>
              </div>
            ))}

            {/* Completed GIFs */}
            {completedGifs.map(gif => (
              <div key={gif.job_id} className="p-3 border-b border-gray-800 bg-green-900/10">
                <div className="flex items-start gap-3">
                  <div className="mt-1">
                    <Check className="w-4 h-4 text-green-500" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-white text-sm">GIF Ready!</p>
                    <div className="flex gap-2 mt-2">
                      <button
                        onClick={() => {
                          onGifReady?.(gif);
                          dismissCompletedGif(gif.job_id);
                        }}
                        className="text-xs px-2 py-1 bg-green-600 text-white rounded hover:bg-green-500"
                      >
                        View
                      </button>
                      <button
                        onClick={() => dismissCompletedGif(gif.job_id)}
                        className="text-xs px-2 py-1 bg-gray-700 text-gray-300 rounded hover:bg-gray-600"
                      >
                        Dismiss
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}

            {/* Empty State */}
            {activeJobs.length === 0 && completedGifs.length === 0 && (
              <div className="p-4 text-center text-gray-500 text-sm">
                No active GIF generations
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default GifProgressIndicator;
